import { ReactNode, useState } from 'react'
import { toast } from 'react-hot-toast'
import { LuChevronLeft } from 'react-icons/lu'

import { useRouter } from 'next/router'

import { Card } from '@ui/components/shadcn/ui/card'

import { AuthTap, useAuthAnalytics } from '@web-app/components/auth/analytics'
import { CreateAccountForm } from '@web-app/components/auth/CreateAccountForm'
import { EmailLoginForm } from '@web-app/components/auth/EmailLoginForm'
import { ForgotPasswordForm } from '@web-app/components/auth/ForgotPasswordForm'
import { IdentifierForm } from '@web-app/components/auth/IdentifierForm'
import { PhoneLoginForm } from '@web-app/components/auth/PhoneLoginForm'
import { SIGN_UP_ANCHOR } from '@web-app/utils/auth/constants'

// The whole pre-auth flow as one card: steps flip in place, and the only
// navigations are the two identity boundaries (register and login success),
// where a push re-runs getServerSideProps so the session reaches the client.
type AuthStep =
  | { name: 'identifier' }
  | { name: 'createAccount'; email?: string; phone?: string }
  | { name: 'loginEmail'; email?: string }
  | { name: 'loginPhone'; phone?: string; smsSentAt?: number }
  | { name: 'forgotPassword'; email?: string }

const HEADINGS: Record<AuthStep['name'], { title: string; sub?: string }> = {
  identifier: { title: 'Start earning', sub: 'Sign up or sign in' },
  createAccount: { title: 'Create your account' },
  loginEmail: { title: 'Welcome back' },
  loginPhone: { title: 'Sign in with your phone' },
  forgotPassword: { title: 'Reset your password' },
}

// Every step can back out: forgot-password returns to the password view it
// came from; everything else returns to the identifier.
const BACK_TAPS: Partial<Record<AuthStep['name'], AuthTap>> = {
  createAccount: 'createAccountBack',
  loginEmail: 'loginPasswordBack',
  loginPhone: 'loginSmsCodeBack',
}

export function AuthCard({ redirect }: { redirect: string | undefined }) {
  const router = useRouter()
  const { trackTap } = useAuthAnalytics()
  const [step, setStep] = useState<AuthStep>({ name: 'identifier' })

  const goBack = () => {
    const tap = BACK_TAPS[step.name]
    if (tap) trackTap(tap)
    setStep(
      step.name === 'forgotPassword'
        ? { name: 'loginEmail', email: step.email }
        : { name: 'identifier' },
    )
  }

  // Awaited by the submitting form so the spinner holds until the new page
  // takes over. The route names the destination.
  const finishLogin = async (destination: string) => {
    toast.success('Successfully logged in!')
    await router.push(destination)
  }

  const toSignIn = (identifier: { kind: 'email' | 'phone'; value: string }) =>
    setStep(
      identifier.kind === 'email'
        ? { name: 'loginEmail', email: identifier.value }
        : { name: 'loginPhone', phone: identifier.value },
    )

  let body: ReactNode
  switch (step.name) {
    case 'identifier':
      body = (
        <IdentifierForm
          onSignUp={(identifier) =>
            setStep({
              name: 'createAccount',
              email: identifier.kind === 'email' ? identifier.value : undefined,
              phone: identifier.kind === 'phone' ? identifier.value : undefined,
            })
          }
          onSignIn={({ identifier, smsSentAt }) =>
            setStep(
              identifier.kind === 'email'
                ? { name: 'loginEmail', email: identifier.value }
                : { name: 'loginPhone', phone: identifier.value, smsSentAt },
            )
          }
        />
      )
      break
    case 'createAccount':
      body = (
        <CreateAccountForm
          initialEmail={step.email}
          initialPhone={step.phone}
          redirect={redirect}
          onSuccess={async (destination) => {
            toast.success('Successfully registered!')
            await router.push(destination)
          }}
          onExistingAccount={toSignIn}
        />
      )
      break
    case 'loginEmail':
      body = (
        <EmailLoginForm
          initialEmail={step.email}
          redirect={redirect}
          onSuccess={finishLogin}
          onForgotPassword={({ email }) =>
            setStep({ name: 'forgotPassword', email })
          }
          onUsePhone={() => setStep({ name: 'loginPhone' })}
        />
      )
      break
    case 'loginPhone':
      body = (
        <PhoneLoginForm
          initialPhone={step.phone}
          smsSentAt={step.smsSentAt}
          redirect={redirect}
          onSuccess={finishLogin}
          onUseEmail={() => setStep({ name: 'loginEmail' })}
        />
      )
      break
    case 'forgotPassword':
      body = (
        <ForgotPasswordForm
          initialEmail={step.email}
          onBackToSignIn={() =>
            setStep({ name: 'loginEmail', email: step.email })
          }
        />
      )
      break
  }

  const heading = HEADINGS[step.name]

  return (
    <Card
      id={SIGN_UP_ANCHOR}
      className="rounded-lg border-0 px-7 py-8 shadow-lg"
    >
      {step.name !== 'identifier' && (
        <button
          type="button"
          onClick={goBack}
          className="text-muted-foreground hover:text-foreground -mt-2 mb-2 flex items-center gap-1 text-sm"
        >
          <LuChevronLeft className="size-4" />
          Back
        </button>
      )}
      <div className="mb-10 text-center">
        <h2 className="font-display text-primary text-2xl md:text-3xl">
          {heading.title}
        </h2>
        {heading.sub && <p className="text-primary/80 mt-1">{heading.sub}</p>}
      </div>
      {body}
    </Card>
  )
}
